import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HandCoins, CheckCircle2, MessageCircle, CalendarClock } from 'lucide-react';
import { getDebts, Debt, money } from '../finances/api';
import PersonAvatar from '../../components/PersonAvatar';
import { teacherPhotoUrl } from '../teachers/api';
import { Skeleton } from '../../components/Skeleton';
import { readCache, writeCache } from '../../services/localCache';
import { useAuth } from '../../context/AuthContext';

// Dos pestañas (Mabel 2-oct-2026: «separar las deudas de AyB y mensualidad»): lo que se debe de la
// vitrina (Alimentos y Bebidas, `product`) va aparte de mensualidades/clases/inscripciones/otros.
// DESFASADOS (Mabel 9/10-oct-2026, audios): tercer cuadro, aparte de los otros dos. Cada uno aparece 2 DÍAS
// ANTES de su fecha (naranja) y en rojo desde el día que le toca pagar, con su recordatorio por WhatsApp.
// Antes de eso no sale en ningún lado del Inicio (ni suma), para no revolverlo con los deudores.
// Mabel 12-oct-2026 (captura): «que se vieran en la pantalla principal pero aparte del cuadro por cobrar,
// no junto con esto» → mismo componente en modo 'desfasados' = su propia tarjeta en el Inicio.
type Grupo = 'mens' | 'ayb' | 'desf';
const DIAS_ANTES = 3;
const esDesfasado = (d: Debt) => d.due_rule_applied === 'desfasado';
const grupoDe = (d: Debt): Grupo => (esDesfasado(d) ? 'desf' : d.debt_type === 'product' ? 'ayb' : 'mens');
// TEXTO PROVISIONAL del recordatorio: Mabel quedó de pasar el suyo (10-oct). Cambiarlo aquí cuando llegue.
const recordatorio = (nombre: string, total: number, fecha: string | null) =>
  `Hola ${nombre.split(' ')[0]}, te saludamos de Let's Move 💃 Te recordamos que ${fecha ? `el ${fechaCorta(fecha)}` : 'pronto'} te toca tu pago de ${money(total)}. ¡Gracias!`;
function fechaCorta(ymd: string) {
  const d = new Date(`${ymd}T12:00:00`);
  return isNaN(d.getTime()) ? ymd : d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
}
function waLink(prefix: string | null | undefined, number: string | null | undefined, texto: string) {
  const num = String(number || '').replace(/\D/g, '');
  if (num.length < 10) return null;
  const pre = String(prefix || '52').replace(/\D/g, '') || '52';
  return `https://wa.me/${pre}${num.slice(-10)}?text=${encodeURIComponent(texto)}`;
}

type Cached = { debts: Debt[]; summary: { total_open: number; students_open: number; staff_open?: number } | null };

export default function ReceivablesPanel({ modo = 'deudores' }: { modo?: 'deudores' | 'desfasados' } = {}) {
  const soloDesf = modo === 'desfasados';
  const { hasModule } = useAuth();
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const cached = readCache<Cached>('receivables');
  const [debts, setDebts] = useState<Debt[] | null>(cached?.debts ?? null);
  const [, setSummary] = useState<Cached['summary']>(cached?.summary ?? null);
  const [grupoSel, setGrupo] = useState<Grupo>('mens');
  const grupo: Grupo = soloDesf ? 'desf' : grupoSel;
  const [q, setQ] = useState('');

  useEffect(() => {
    getDebts().then((r) => {
      setDebts(r.data); setSummary(r.summary);
      writeCache('receivables', { debts: r.data, summary: r.summary });
    }).catch(() => setDebts((prev) => prev ?? []));
  }, []);

  // Agrupar por alumno y ordenar por monto adeudado
  const agrupar = (g: Grupo) => {
    if (!debts) return [];
    const m = new Map<string, { name: string; account_id: number; has_photo: number; total: number; maestro: boolean; profe: boolean; dias: number | null; fecha: string | null; tel: { p?: string | null; n?: string | null } }>();
    for (const d of debts) {
      // Maestros (fiados de vitrina, `payer_user_id`): también se cobran (Carlos 29-sep). Clave con
      // prefijo porque los ids de users y contacts chocan.
      const esMaestro = !d.contact_id && !!d.payer_user_id;
      if (!d.contact_id && !esMaestro) continue;
      if (grupoDe(d) !== g) continue;
      // Desfasado que todavía no entra en su ventana de DIAS_ANTES días: aún no se le cobra, no se enseña.
      if (esDesfasado(d) && (d.days_to_due == null || Number(d.days_to_due) > DIAS_ANTES)) continue;
      const key = esMaestro ? `u-${d.payer_user_id}` : `c-${d.contact_id}`;
      const prev = m.get(key) || { name: d.student_name, account_id: esMaestro ? Number(d.payer_user_id) : Number(d.contact_id), has_photo: d.has_photo, total: 0, maestro: esMaestro, profe: esMaestro && !!d.payer_es_profe, dias: null as number | null, fecha: null as string | null, tel: { p: d.phone_prefix, n: d.phone_number } };
      // `remaining` (amount − abonos), no `amount`: con el importe completo, quien ya abonó una
      // parte aparecía aquí debiendo todo. Hay 22 alumnas con abonos adelantados (12-ago-2026),
      // así que no es un caso raro. El backend ya calcula remaining para esto mismo.
      prev.total += Number(d.remaining ?? d.amount ?? 0);
      // El adeudo MÁS vencido manda (days_to_due = due_date − hoy; negativo = vencido).
      if (d.days_to_due != null && (prev.dias == null || Number(d.days_to_due) < prev.dias)) { prev.dias = Number(d.days_to_due); prev.fecha = d.due_date; }
      m.set(key, prev);
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  };
  const porGrupo = { mens: agrupar('mens'), ayb: agrupar('ayb'), desf: agrupar('desf') };
  const totalDe = (g: Grupo) => porGrupo[g].reduce((acc, x) => acc + x.total, 0);
  const byStudent = porGrupo[grupo];
  // Mabel 9-oct-2026: la lista enseñaba solo los 8 que más deben y las cajeras (sin Finanzas) no tenían
  // dónde ver al resto. Ahora van TODOS (la lista ya hace scroll) y con buscador cuando son muchos.
  const norm = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const visibles = q.trim() ? byStudent.filter((s) => norm(s.name || '').includes(norm(q.trim()))) : byStudent;
  const alumnos = byStudent.filter((x) => !x.maestro).length;
  const maestros = byStudent.filter((x) => x.maestro && x.profe).length;
  const equipo = byStudent.length - alumnos - maestros;
  const quienes = [
    alumnos > 0 && `${alumnos} alumno${alumnos === 1 ? '' : 's'}`,
    maestros > 0 && `${maestros} maestro${maestros === 1 ? '' : 's'}`,
    equipo > 0 && `${equipo} del equipo`,
  ].filter(Boolean).join(' y ');
  const hayAlgo = soloDesf ? porGrupo.desf.length > 0 : porGrupo.mens.length + porGrupo.ayb.length > 0;
  // Próximo desfasado que aún no entra en su ventana: para que la tarjeta vacía diga cuándo sale el siguiente.
  const proximoDesf = soloDesf && debts
    ? debts.filter((d) => esDesfasado(d) && d.days_to_due != null && Number(d.days_to_due) > DIAS_ANTES)
        .sort((a, b) => Number(a.days_to_due) - Number(b.days_to_due))[0] ?? null
    : null;

  return (
    <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          {soloDesf
            ? <><CalendarClock size={16} className="text-amber-600 dark:text-amber-400" /> Desfasados</>
            : <><HandCoins size={16} className="text-accent-ink" /> Por cobrar</>}
        </h3>
        {/* Por cobrar: solo con Finanzas. Desfasados (Mabel 4-nov-2026, #1649): la cajera (Caja sin Finanzas)
            también tiene a dónde ir → /desfasados, la misma lista del mes sin el resto de Finanzas. */}
        {(hasModule('letsmove_finances') || (soloDesf && hasModule('cashbox'))) && (
          <Link to={soloDesf ? (hasModule('letsmove_finances') ? '/finanzas?tab=adeudos&ver=desfasados' : '/desfasados') : '/finanzas'}
            data-testid={soloDesf ? 'desfasados-ver-adeudos' : undefined} className="text-xs font-medium text-brand-blue hover:underline">
            {soloDesf ? 'Ver desfasados del mes →' : 'Ver adeudos →'}
          </Link>
        )}
      </div>

      {debts === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : !hayAlgo && soloDesf ? (
        <div className="py-4 text-center" data-testid="desfasados-vacio">
          <p className="text-sm font-medium text-content">Nadie tiene que pagar en estos días</p>
          <p className="mt-0.5 text-xs text-content-3">
            {proximoDesf
              ? `Cada uno sale aquí ${DIAS_ANTES} días antes de su fecha. El próximo: ${proximoDesf.student_name}, el ${fechaCorta(proximoDesf.due_date || '')}.`
              : `Cada uno sale aquí ${DIAS_ANTES} días antes de su fecha.`}
          </p>
        </div>
      ) : !hayAlgo ? (
        <div className="flex flex-col items-center gap-1 py-6 text-center">
          <CheckCircle2 size={26} className="text-emerald-500" />
          <p className="text-sm font-medium text-content">Todo al día</p>
          <p className="text-xs text-content-3">No hay adeudos abiertos.</p>
        </div>
      ) : (
        <>
          {!soloDesf && <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-surface-2 p-1" role="tablist">
            {([['mens', 'Mensualidades'], ['ayb', 'Alimentos y bebidas']] as const).map(([g, label]) => (
              <button key={g} type="button" role="tab" aria-selected={grupo === g} onClick={() => setGrupo(g)}
                data-testid={`por-cobrar-tab-${g}`}
                className={`rounded-lg px-2 py-1.5 text-left transition ${grupo === g ? 'bg-surface shadow-sm' : 'hover:bg-surface/60'}`}>
                <span className="block truncate text-[11px] font-medium text-content-3">{label}</span>
                <span className={`block text-sm font-semibold tabular-nums ${grupo === g ? 'text-brand-blue' : 'text-content'}`}>{money(totalDe(g))}</span>
              </button>
            ))}
          </div>}
          <div className="mb-3 flex items-baseline gap-2">
            <span className={`font-display text-2xl font-bold ${soloDesf ? (porGrupo.desf.some((x) => (x.dias ?? 1) <= 0) ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400') : 'text-brand-blue'}`}>{money(totalDe(grupo))}</span>
            <span className="text-xs text-content-3">
              {byStudent.length === 0 ? 'Nadie debe aquí' : <>
                {quienes} {byStudent.length === 1 ? 'debe' : 'deben'}
              </>}
            </span>
          </div>
          {/* pb-3: la moneda de adeudo va abajo a la derecha del avatar y su halo late hasta ~8 px por
              debajo; sin este aire, el último renglón hacía aparecer y desaparecer la barra de
              desplazamiento a cada latido (Carlos 29-sep, «sube y baja» con 2 renglones). */}
          {byStudent.length > 8 && (
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre…"
              data-testid="por-cobrar-buscar"
              className="mb-2 w-full rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-sm text-content placeholder:text-content-3 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" />
          )}
          {q.trim() && visibles.length === 0 && <p className="py-3 text-center text-xs text-content-3">Nadie con ese nombre debe aquí.</p>}
          <ul className="-mr-1 max-h-72 space-y-1 overflow-y-auto pb-3 pr-1 [scrollbar-width:thin]">
            {visibles.map((s) => {
              // Equipo que NO da clases (gerencia, recepción): no tiene ficha de profesor → sin enlace
              // (antes iba a /profesores/:id → 404 y carga eterna; Mabel 2-oct-2026).
              const sinFicha = s.maestro && !s.profe;
              const Fila = ({ children }: { children: React.ReactNode }) => sinFicha
                ? <div className="flex items-center gap-2.5 rounded-lg px-1 py-1" data-testid="por-cobrar-equipo">{children}</div>
                : <Link to={s.maestro ? `/profesores/${s.account_id}` : `/alumnos/${s.account_id}`}
                    className="flex items-center gap-2.5 rounded-lg px-1 py-1 transition hover:bg-surface-2" data-testid={s.maestro ? 'por-cobrar-maestro' : undefined}>{children}</Link>;
              return (
              <Fila key={`${s.maestro ? 'u' : 'c'}-${s.account_id}`}>
                {s.maestro
                  ? <PersonAvatar userId={s.account_id} hasPhoto={s.has_photo} photoSrc={teacherPhotoUrl(s.account_id)} name={s.name} size="h-8 w-8" />
                  : <PersonAvatar contactId={s.account_id} hasPhoto={s.has_photo} name={s.name} size="h-8 w-8" />}
                {/* La etiqueta va FUERA del texto truncado: con nombres largos en el celular se cortaba. */}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-content">{s.name}</span>
                  {grupo === 'desf' && s.dias != null ? (
                    <span className={`block text-[11px] font-medium ${s.dias <= 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} data-testid="desfasado-estado">
                      {s.dias < 0 ? `Le tocaba el ${fechaCorta(s.fecha || '')} · hace ${-s.dias} día${s.dias === -1 ? '' : 's'}` : s.dias === 0 ? 'Le toca pagar HOY' : `Le toca el ${fechaCorta(s.fecha || '')}`}
                    </span>
                  ) : s.dias != null && (
                    <span className={`block text-[11px] ${s.dias < 0 ? 'font-medium text-red-600 dark:text-red-400' : 'text-content-3'}`} data-testid="por-cobrar-dias">
                      {s.dias < 0 ? `Vencido hace ${-s.dias} día${s.dias === -1 ? '' : 's'}` : s.dias === 0 ? 'Vence hoy' : `Vence en ${s.dias} día${s.dias === 1 ? '' : 's'}`}
                    </span>
                  )}
                </span>
                {s.maestro && <span className="shrink-0 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">{s.profe ? 'maestro' : 'equipo'}</span>}
                <span className="shrink-0 text-sm font-semibold tabular-nums text-content">{money(s.total)}</span>
                {grupo === 'desf' && (() => {
                  const url = waLink(s.tel.p, s.tel.n, recordatorio(s.name, s.total, s.fecha));
                  return url ? (
                    // Botón (no <a>): la fila ya es un enlace a la ficha y no se anidan enlaces.
                    <button type="button" data-testid="desfasado-wa" title="Mandar recordatorio por WhatsApp"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(url, '_blank', 'noopener'); }}
                      className="shrink-0 rounded-lg bg-emerald-500/15 p-1.5 text-emerald-700 transition hover:bg-emerald-500/25 dark:text-emerald-300">
                      <MessageCircle size={15} />
                    </button>
                  ) : <span className="shrink-0 text-[10px] text-content-3" title="Sin teléfono en su ficha">sin tel.</span>;
                })()}
              </Fila>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
